import { html, nothing } from "lit";
import type { ContextUsageSnapshot } from "../chat/chat-context-usage.ts";
import { renderContextUsageRing } from "../chat/context-usage-ring.ts";
import type { ContextBreakdownCategoryId } from "../controllers/chat-context-breakdown.ts";
import {
  formatBreakdownTokens,
  type SessionContextBreakdown,
} from "../controllers/chat-context-breakdown.ts";

const SEGMENT_CLASS: Record<ContextBreakdownCategoryId, string> = {
  systemPrompt: "system",
  tools: "tools",
  rules: "rules",
  skills: "skills",
  mcp: "mcp",
  subagents: "subagents",
  summarizedConversation: "summarized",
  conversation: "conversation",
};

function pct(part: number, total: number): number {
  if (total <= 0) {
    return 0;
  }
  return (part / total) * 100;
}

export type FleetRunContextSummaryProps = {
  connected: boolean;
  runActive: boolean;
  contextUsage: ContextUsageSnapshot;
  breakdownLoading?: boolean;
  breakdownError?: string | null;
  breakdown?: SessionContextBreakdown | null;
  onOpenBreakdown?: () => void;
  onRefreshBreakdown?: () => void;
  turnPhase?: string | null;
  sessionHealth?: "healthy" | "suspect" | "wedged" | null;
  sessionHealthReason?: string | null;
  lastSignalAgeMs?: number | null;
};

export function renderFleetRunContextSummary(props: FleetRunContextSummaryProps) {
  const { contextUsage } = props;
  const breakdown = props.breakdown;
  const barTotal = breakdown
    ? Math.max(
        breakdown.usedTokens,
        breakdown.categories.reduce((s, c) => s + c.tokens, 0),
        1,
      )
    : 1;
  const categoriesWithTokens = breakdown?.categories.filter((c) => c.tokens > 0) ?? [];

  const healthClass =
    props.sessionHealth === "wedged"
      ? "fleet-run-context-summary__signal--wedged"
      : props.sessionHealth === "suspect"
        ? "fleet-run-context-summary__signal--suspect"
        : "fleet-run-context-summary__signal--ok";

  return html`
    <section class="fleet-run-context-summary" aria-label="Session context usage">
      ${
        props.turnPhase || props.sessionHealth
          ? html`
              <div class="fleet-run-context-summary__signals">
                ${
                  props.turnPhase
                    ? html`<span class="fleet-run-context-summary__signal mono"
                        >Turn: ${props.turnPhase.replace(/_/g, " ")}</span
                      >`
                    : nothing
                }
                ${
                  props.sessionHealth && props.sessionHealth !== "healthy"
                    ? html`<span class="fleet-run-context-summary__signal ${healthClass}"
                        >${props.sessionHealth}${
                          props.sessionHealthReason
                            ? html` · ${props.sessionHealthReason}`
                            : nothing
                        }</span
                      >`
                    : nothing
                }
                ${
                  props.lastSignalAgeMs != null
                    ? html`<span class="fleet-run-context-summary__signal muted mono"
                        >Last signal ${Math.max(0, Math.floor(props.lastSignalAgeMs / 1000))}s
                        ago</span
                      >`
                    : nothing
                }
              </div>
            `
          : nothing
      }
      <header class="fleet-run-context-summary__header">
        <div class="fleet-run-context-summary__title-row">
          ${renderContextUsageRing(contextUsage.percent, { size: 40 })}
          <div>
            <h4 class="fleet-run-context-summary__title">Context window</h4>
            <p class="fleet-run-context-summary__tokens mono">
              ${contextUsage.label}
              ${
                contextUsage.percent != null
                  ? html`<span class="muted"> · ${contextUsage.percent}%</span>`
                  : nothing
              }
            </p>
            ${
              contextUsage.modelLabel
                ? html`<p class="fleet-run-context-summary__model muted mono">
                    ${contextUsage.modelLabel}
                  </p>`
                : nothing
            }
          </div>
        </div>
        <div class="fleet-run-context-summary__actions">
          ${
            props.onRefreshBreakdown
              ? html`
                  <button
                    type="button"
                    class="btn subtle btn--sm"
                    ?disabled=${!props.connected || props.breakdownLoading}
                    @click=${props.onRefreshBreakdown}
                  >
                    ${props.breakdownLoading ? "Loading…" : "Refresh"}
                  </button>
                `
              : nothing
          }
          ${
            props.onOpenBreakdown
              ? html`
                  <button
                    type="button"
                    class="btn btn--sm"
                    ?disabled=${!props.connected}
                    @click=${props.onOpenBreakdown}
                  >
                    Full breakdown
                  </button>
                `
              : nothing
          }
        </div>
      </header>

      ${
        props.runActive
          ? html`
              <p class="fleet-run-context-summary__hint muted" role="status">
                Context fill updates when this run finishes.
              </p>
            `
          : nothing
      }
      ${
        props.breakdownError
          ? html`<p class="fleet-run-context-summary__error">${props.breakdownError}</p>`
          : props.breakdownLoading && !breakdown
            ? html` <p class="muted fleet-run-context-summary__status">Loading breakdown…</p> `
            : breakdown && categoriesWithTokens.length > 0
              ? html`
                  <div
                    class="fleet-run-context-summary__bar context-stacked-bar"
                    aria-hidden="true"
                  >
                    ${categoriesWithTokens.map(
                      (c) => html`
                        <div
                          class="context-segment ${SEGMENT_CLASS[c.id]}"
                          style="width: ${pct(c.tokens, barTotal).toFixed(1)}%"
                          title="${c.label}: ~${formatBreakdownTokens(c.tokens)}"
                        ></div>
                      `,
                    )}
                  </div>
                  <p class="muted fleet-run-context-summary__source">
                    Source: ${breakdown.reportSource === "run" ? "last agent run" : "estimate"}
                  </p>
                `
              : html`
                  <p class="muted fleet-run-context-summary__status">
                    ${
                      props.connected
                        ? "Refresh or open the full breakdown to see how tokens are allocated."
                        : "Connect to the gateway to load context details."
                    }
                  </p>
                `
      }
    </section>
  `;
}
